/**
 * Shared status dot — the one glyph that says "this thing is running".
 *
 * Every surface that reflects the state of a long-lived process (status-bar
 * items, sidebar cards, server popovers) uses this instead of drawing its own
 * circle or, worse, spelling the state out with a ▶/▷ character in a label.
 * Colours are the card standard's, so existing sidebars do not shift.
 *
 * The pulse is opt-in: a single item that owns the user's attention (a status
 * bar entry, one server row) reads better breathing, while a list of ten
 * running rows all blinking at once does not.
 */

import type { shell } from 'punica';

import statusDotCss from './statusDot.css';

type StatusDotState = shell.View.Helpers.StatusDotState;
type StatusDotOptions = shell.View.Helpers.StatusDotOptions;

const STYLE_ID = 'punica-status-dot-styles';

/** Inject the dot styles once, so no consumer ships its own copy. */
export function ensureStatusDotStyles(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = statusDotCss;
  document.head.appendChild(style);
}

/** Build one status dot. */
export function createStatusDot(
  state: StatusDotState,
  options?: StatusDotOptions
): HTMLElement {
  ensureStatusDotStyles();
  const dot = document.createElement('span');
  let cls = `punica-status-dot punica-status-dot--${state}`;
  if (options?.pulse) cls += ' punica-status-dot--pulse';
  if (options?.className) cls += ` ${options.className}`;
  dot.className = cls;
  return dot;
}

/**
 * States that mean "work is happening right now" — the ones worth animating.
 * Kept here so the status bar and any other caller agree on the answer.
 */
export function isActiveState(state: StatusDotState): boolean {
  return state === 'running' || state === 'busy';
}

export type { StatusDotState };
